import { useEffect, useMemo, useRef, useState } from 'react';
import { Crop, Loader2, RotateCcw } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';

const DEFAULT_ASPECT_RATIO = 5 / 4;
const PREVIEW_WIDTH = 500;
const PREVIEW_HEIGHT = 400;
const EDITOR_MAX_WIDTH = 560;
const EDITOR_MAX_HEIGHT = 420;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getBaseCrop(imageWidth, imageHeight, aspectRatio) {
  if (imageWidth / imageHeight > aspectRatio) {
    return {
      width: imageHeight * aspectRatio,
      height: imageHeight,
    };
  }

  return {
    width: imageWidth,
    height: imageWidth / aspectRatio,
  };
}

function getCropRect(imageWidth, imageHeight, aspectRatio, zoom, offsetX, offsetY) {
  const baseCrop = getBaseCrop(imageWidth, imageHeight, aspectRatio);
  const cropWidth = baseCrop.width / zoom;
  const cropHeight = baseCrop.height / zoom;
  const maxOffsetX = Math.max(0, (imageWidth - cropWidth) / 2);
  const maxOffsetY = Math.max(0, (imageHeight - cropHeight) / 2);
  const centerX = imageWidth / 2 + maxOffsetX * (offsetX / 100);
  const centerY = imageHeight / 2 + maxOffsetY * (offsetY / 100);

  return {
    sx: clamp(centerX - cropWidth / 2, 0, imageWidth - cropWidth),
    sy: clamp(centerY - cropHeight / 2, 0, imageHeight - cropHeight),
    sw: cropWidth,
    sh: cropHeight,
  };
}

export default function ImageCropUploadDialog({
  open,
  onOpenChange,
  file,
  onConfirm,
  inline = false,
  title = "Recadrer l'image",
  description = "Ajustez le cadrage avant l'upload. Le format 5:4 garantit un rendu homogène sur les pages prestations.",
  aspectRatio = DEFAULT_ASPECT_RATIO,
}) {
  const canvasRef = useRef(null);
  const [sourceImage, setSourceImage] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const previewUrl = useMemo(() => {
    if (!file) return null;
    return URL.createObjectURL(file);
  }, [file]);

  const cropRect = useMemo(() => {
    if (!sourceImage) return null;

    return getCropRect(
      sourceImage.width,
      sourceImage.height,
      aspectRatio,
      zoom,
      offsetX,
      offsetY
    );
  }, [sourceImage, aspectRatio, zoom, offsetX, offsetY]);

  const editorMetrics = useMemo(() => {
    if (!sourceImage) return null;

    const scale = Math.min(
      EDITOR_MAX_WIDTH / sourceImage.width,
      EDITOR_MAX_HEIGHT / sourceImage.height,
      1
    );

    return {
      width: sourceImage.width * scale,
      height: sourceImage.height * scale,
      scale,
    };
  }, [sourceImage]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  useEffect(() => {
    if (!open || !previewUrl) {
      setSourceImage(null);
      setZoom(1);
      setOffsetX(0);
      setOffsetY(0);
      return;
    }

    setIsImageLoading(true);
    const image = new Image();
    image.onload = () => {
      setSourceImage(image);
      setIsImageLoading(false);
    };
    image.onerror = () => {
      setSourceImage(null);
      setIsImageLoading(false);
    };
    image.src = previewUrl;
  }, [open, previewUrl]);

  useEffect(() => {
    if (!sourceImage || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    if (!context) return;

    canvas.width = PREVIEW_WIDTH;
    canvas.height = PREVIEW_HEIGHT;

    const { sx, sy, sw, sh } = cropRect;

    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(sourceImage, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
  }, [sourceImage, cropRect]);

  const resetCrop = () => {
    setZoom(1);
    setOffsetX(0);
    setOffsetY(0);
  };

  const handleConfirm = async () => {
    if (!sourceImage || !canvasRef.current || !file || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
      const quality = outputType === 'image/png' ? undefined : 0.92;

      const croppedFile = await new Promise((resolve, reject) => {
        canvasRef.current.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Failed to generate cropped image'));
            return;
          }

          const extension = outputType === 'image/png' ? 'png' : 'jpg';
          const fileName = file.name.replace(/\.[^.]+$/, '') || 'service-image';
          resolve(new File([blob], `${fileName}-cropped.${extension}`, { type: outputType }));
        }, outputType, quality);
      });

      await onConfirm(croppedFile);
    } finally {
      setIsSubmitting(false);
    }
  };

  const content = (
    <>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crop size={18} />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="rounded-sm border border-[var(--champagne)] bg-[var(--champagne)]/10 p-4">
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(280px,0.95fr)] lg:items-start">
              <div>
                <p className="mb-2 text-xs uppercase tracking-[0.2em] text-[var(--charcoal)]/55">
                  Image originale
                </p>
                <div className="flex min-h-[240px] items-center justify-center border border-[var(--champagne)] bg-[#111]/5 p-3 shadow-sm">
                  {isImageLoading ? (
                    <div className="flex h-full min-h-[220px] items-center justify-center text-[var(--charcoal)]/50">
                      <Loader2 size={24} className="animate-spin" />
                    </div>
                  ) : sourceImage && editorMetrics && cropRect ? (
                    <div
                      className="relative overflow-hidden bg-white"
                      style={{
                        width: `${editorMetrics.width}px`,
                        height: `${editorMetrics.height}px`,
                      }}
                    >
                      <img
                        src={previewUrl}
                        alt="Source"
                        className="block h-full w-full object-contain"
                      />
                      <div
                        className="pointer-events-none absolute border-2 border-[var(--gold)] shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]"
                        style={{
                          left: `${cropRect.sx * editorMetrics.scale}px`,
                          top: `${cropRect.sy * editorMetrics.scale}px`,
                          width: `${cropRect.sw * editorMetrics.scale}px`,
                          height: `${cropRect.sh * editorMetrics.scale}px`,
                        }}
                      />
                    </div>
                  ) : null}
                </div>
                <p className="mt-2 text-xs text-[var(--charcoal)]/50">
                  Tu cadres ici sur la photo d’origine. Le rectangle doré montre la zone conservée.
                </p>
              </div>

              <div className="mx-auto w-full max-w-[320px]">
                <p className="mb-2 text-xs uppercase tracking-[0.2em] text-[var(--charcoal)]/55">
                  Aperçu final
                </p>
                <div className="aspect-[5/4] overflow-hidden border border-[var(--champagne)] bg-white shadow-sm">
                {isImageLoading ? (
                  <div className="flex h-full items-center justify-center text-[var(--charcoal)]/50">
                    <Loader2 size={24} className="animate-spin" />
                  </div>
                ) : (
                  <canvas ref={canvasRef} className="h-full w-full" />
                )}
              </div>
                <p className="mt-2 text-center text-xs text-[var(--charcoal)]/50">
                  Aperçu final du cadrage 5:4
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-5">
            <div>
              <div className="mb-2 flex items-center justify-between">
                <Label>Zoom</Label>
                <span className="text-xs text-[var(--charcoal)]/50">{zoom.toFixed(2)}x</span>
              </div>
              <Slider
                min={1}
                max={3}
                step={0.01}
                value={[zoom]}
                onValueChange={([value]) => setZoom(value)}
              />
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <Label>Décalage horizontal</Label>
                <span className="text-xs text-[var(--charcoal)]/50">{offsetX}%</span>
              </div>
              <Slider
                min={-100}
                max={100}
                step={1}
                value={[offsetX]}
                onValueChange={([value]) => setOffsetX(value)}
              />
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <Label>Décalage vertical</Label>
                <span className="text-xs text-[var(--charcoal)]/50">{offsetY}%</span>
              </div>
              <Slider
                min={-100}
                max={100}
                step={1}
                value={[offsetY]}
                onValueChange={([value]) => setOffsetY(value)}
              />
            </div>
          </div>
        </div>

        <DialogFooter className="mt-2">
          <Button
            type="button"
            variant="outline"
            onClick={resetCrop}
            className="rounded-none"
          >
            <RotateCcw size={14} className="mr-2" />
            Réinitialiser
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-none"
          >
            Annuler
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={!sourceImage || isSubmitting}
            className="rounded-none bg-[var(--gold)] text-white hover:bg-[var(--dark-gold)]"
          >
            {isSubmitting ? (
              <>
                <Loader2 size={14} className="mr-2 animate-spin" />
                Préparation...
              </>
            ) : (
              'Utiliser ce cadrage'
            )}
          </Button>
        </DialogFooter>
    </>
  );

  if (inline) {
    if (!open) return null;
    return (
      <div className="mt-4 space-y-4 border border-[var(--champagne)] bg-white p-4 shadow-sm">
        {content}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[720px]">
        {content}
      </DialogContent>
    </Dialog>
  );
}
